<template>
    <div  class="flex flex-col">
        <div class="-mt-4 mb-4 flex py-3 px-4 text-sm text-pef bg-red-200 border-1 border-pef rounded"
            v-if="! permissionToCreate">
            <icon name="alert-circle" class="mt-0.5 mr-4"/>
            <span v-text="resourceAvailabilityMessage"></span>
        </div>

        <div v-else class="-mt-4 mb-4 flex py-3 px-4 text-sm text-accented bg-blue-100 rounded">
            <icon name="alert-circle" class="mt-0.5 mr-4"/>
            <span>
                Rezervační mód, vyberte v kalendáři čas, který chcete rezervovat.
                 
                Pro rezervaci klikněte / tapněte do kalendáře, držte a potáhněte dolů nebo nahoru.
            </span>
        </div>

        <div class="flex-1">
            <FullCalendar
                class="ReservationFormCalendar"
                ref="ReservationFormCalendar"
                :options="config"/>
        </div>

        <ReservationModal
            :resourceId="resource.id"
            :projectId="project && project.id"
            :selection="selection"
            :slotId="updateReservationModal"
            @close="updateReservationModal = null"/>

    </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import ReservationModal from '@/ReservationModal/Index';
import Notify from '@/utils/Notify';
import { throttle } from 'lodash';

export default {
    components: {
        FullCalendar,
        ReservationModal,
    },

    props: {
        resource: {
            type: Object,
            required: true
        },
        project: {
            type: Object,
            required: false
        },
        permissionToCreate: {
            type: Boolean,
            default: false
        },
        permissionToCreateInPast: {
            type: Boolean,
            default: false
        },
    },

    data() {
        return {
            calendarApi: null,
            firstDayOfWeek: null,
            selection: null,
            updateReservationModal: null,
            dateRangeFilter: null,
        }
    },

    watch: {
        dateRangeFilter(val) {
            this.throttleFetch();
        }
    },

    computed: {
        resourceAvailableForRoleTitles() {
            return this.resource
                .availableForRoles
                .map(role => role.title)
                .join(', ')
            ;
        },
        resourceAvailabilityMessage() {
            return `Rezervace zdroje ${this.resource.title} je povolena pouze pro skupiny ${this.resourceAvailableForRoleTitles} `;
        },
        initialDate() {
            if (! this.resource.first_following_open_slot) {
                console.error('No open slots defined for resource');
                alert('No open slots defined for resource');
                return;
            }
            return this.resource.first_following_open_slot.start;
        },
        openSlots() {
            return this.$store.openSlots
                .map(slot => {
                    return {
                        daysOfWeek: [ slot.numeric_day_of_week ],
                        startTime: slot.time_start,
                        endTime: slot.time_end
                    };
                })
                // if openSlots is an empty array, fullcalendar does not display business hours corectly
                // so we add any tiny slot
                .concat({
                        daysOfWeek: [ 1 ],
                        startTime: '00:00:00',
                        endTime: '00:00:01'

                });
        },
        occupiedSlots() {
            return this.$store.occupiedSlots;
        },
        reservations() {
            return this.$store.reservations;
        },
        config() {
            return {
                ...this.configOptions,
                ...this.eventHandlers,
            }
        },
        configOptions() {
            return {
                plugins: [interactionPlugin, timeGridPlugin],
                initialView: 'timeGridWeek',
                initialDate: this.initialDate,
                firstDay: 1,
                locale: 'cs',
                allDaySlot: false,
                height: '100%',
                headerToolbar: {
                    left: 'prev,next today',
                    center: 'title',
                    right: 'timeGridDay,timeGridWeek'
                },
                buttonText: {
                    today: 'dnes',
                    day: 'den',
                    week: 'týden',
                    month: 'měsíc',
                },

                selectable: true,
                selectConstraint: 'businessHours',

                eventSources: [
                    // slots to be reserved in this reservation
                    {
                        events: this.reservations,
                        color: 'var(--pef)',
                    },
                    // slots already reserved in other reservations
                    {
                        events: this.occupiedSlots,
                    }
                ],
                businessHours: this.openSlots,
            }
        },
        eventHandlers() {
            return {
                datesSet: this.calendarDateRangeChanged,

                select: (info) => {
                    if (! this.permissionToCreate) {
                        return Notify.swal({
                            icon: 'error',
                            title: 'Nemáte oprávnění',
                            text: this.resourceAvailabilityMessage
                        });
                    }

                    if (!this.dateIsRegistrable(info.start) && !this.permissionToCreateInPast) {
                        return Notify.swal({
                            icon: 'error',
                            title: 'Termín nelze rezervovat',
                            text: this.resource.register_days_before
                                ? `Zdroj ${this.resource.title} musí být rezervovaný nejpozději ${this.resource.register_days_before} dní předem`
                                : `Zdroj ${this.resource.title} nelze rezervovat zpětně`
                        });
                    }

                    this.selection = info;
                },

                eventClick: (info) => {
                    this.openUpdateReservationModal(info.event.id);
                }
            }
        }
    },

    methods: {
        refreshDateRangeFilter(info) {
            this.dateRangeFilter = info.start.addDays(-7).toYmdString()
                + ','
                + info.end.addDays(7).toYmdString()
        },

        calendarDateRangeChanged(info) {
            this.refreshDateRangeFilter(info);

            if (this.firstDayOfWeek === info.startStr) {
                return false;
            }

            this.firstDayOfWeek = info.startStr;

            this.fetchOpenSlots();
        },

        async fetchOpenSlots() {
            const response = await axios.get(`/resources/${this.resource.id}/open-slots`, {
                params: {
                    anyDayOfWeek: this.firstDayOfWeek,
                    'filter[following]': false
                }
            });
            this.$store.openSlots = response.data.openSlots;
        },

        async fetchReservations() {
            const response = await axios.get(`/resources/${this.resource.id}/reservations`, {
                params: {
                    'filter[date_between]': this.dateRangeFilter
                }
            });
            this.$store.reservations = response.data.reservations;
        },

        async fetchOccupiedSlots() {
            const response = await axios.get(`/resources/${this.resource.id}/occupied-slots`, {
                params: {
                    'filter[date_between]': this.dateRangeFilter
                }
            });
            this.$store.occupiedSlots = response.data.occupiedSlots;
        },

        throttleFetch: throttle(function () {
            this.fetchReservations();
            this.fetchOccupiedSlots();
        }, 1000),

        openUpdateReservationModal(slotId) {
            this.updateReservationModal = slotId;
        },

        dateIsRegistrable(date) {
            let firstFollowingOpenSlot = new Date(this.initialDate);
            let dateToRegister = new Date(date);
            return firstFollowingOpenSlot.getTime() <= dateToRegister.getTime();
        }
    },

    mounted() {
        this.calendarApi = this.$refs.ReservationFormCalendar.getApi();

        window.events.$on('refreshCalendar', (e) => {
            this.fetchOccupiedSlots();
            this.fetchReservations();
        });
    }
};
</script>
